import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Check as CheckIcon, Upload } from "lucide-react";
import sh from "bundles/common/utils/sh";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";

const ApplyToOfferDialog = ({ open, onClose, candidateId, candidateIds, candidateResumeUrl, appliedOfferIds = [], initialOfferIds = [], onApplied, jobApplication }) => {
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const { enqueueSnackbar } = useSnackbar();

  const isEditMode = !!jobApplication;
  const isBulkMode = !isEditMode && Array.isArray(candidateIds) && candidateIds.length > 1;
  const isCreateMode = !isEditMode && !candidateId && !isBulkMode;

  const [tabValue, setTabValue] = useState(0);
  const [coverLetter, setCoverLetter] = useState("Sent by talent team");
  const [offerSearch, setOfferSearch] = useState("");
  const [offerResults, setOfferResults] = useState([]);
  const [searchingOffers, setSearchingOffers] = useState(false);
  const [applying, setApplying] = useState(false);
  const [selectedOfferIds, setSelectedOfferIds] = useState([]);
  const [selectedOffers, setSelectedOffers] = useState([]);

  const [companySearch, setCompanySearch] = useState("");
  const [companyResults, setCompanyResults] = useState([]);
  const [searchingCompanies, setSearchingCompanies] = useState(false);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [resumeFile, setResumeFile] = useState(null);
  const cvInputRef = useRef(null);

  const [candidateSearch, setCandidateSearch] = useState("");
  const [candidateResults, setCandidateResults] = useState([]);
  const [searchingCandidates, setSearchingCandidates] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  const effectiveCandidateId = candidateId || (isCreateMode ? selectedCandidate?.id : null);
  const effectiveResumeUrl = candidateResumeUrl || (isCreateMode ? selectedCandidate?.resume_url : null);

  const [fullJobApplication, setFullJobApplication] = useState(null);
  const [loadingFull, setLoadingFull] = useState(false);

  useEffect(() => {
    if (open && isEditMode && jobApplication?.id) {
      setLoadingFull(true);
      sh.get(`/internal/job_applications/${jobApplication.id}`)
        .then((res) => {
          const full = res.data;
          setFullJobApplication(full);
          setCoverLetter(full.cover_letter || "");
        })
        .catch(() => {
          setFullJobApplication(null);
          setCoverLetter(jobApplication.cover_letter || "");
        })
        .finally(() => setLoadingFull(false));
    }
  }, [open, isEditMode, jobApplication?.id]);

  useEffect(() => {
    if (open && !isEditMode && initialOfferIds.length > 0) {
      const filtered = initialOfferIds.filter((id) => !appliedOfferIds.includes(id));
      setSelectedOfferIds(filtered);
    }
  }, [open]);

  const handleClose = () => {
    setTabValue(0);
    setOfferSearch("");
    setOfferResults([]);
    setCoverLetter("Sent by talent team");
    setSelectedOfferIds([]);
    setSelectedOffers([]);
    setCompanySearch("");
    setCompanyResults([]);
    setSelectedCompanyIds([]);
    setSelectedCompanies([]);
    setResumeFile(null);
    setFullJobApplication(null);
    setCandidateSearch("");
    setCandidateResults([]);
    setSelectedCandidate(null);
    onClose();
  };

  const toggleOfferSelection = (jo) => {
    setSelectedOfferIds((prev) => prev.includes(jo.id) ? prev.filter((x) => x !== jo.id) : [...prev, jo.id]);
    setSelectedOffers((prev) => prev.some((o) => o.id === jo.id) ? prev.filter((o) => o.id !== jo.id) : [...prev, jo]);
  };

  const toggleCompanySelection = (company) => {
    setSelectedCompanyIds((prev) => prev.includes(company.id) ? prev.filter((x) => x !== company.id) : [...prev, company.id]);
    setSelectedCompanies((prev) => prev.some((c) => c.id === company.id) ? prev.filter((c) => c.id !== company.id) : [...prev, company]);
  };

  const handleUpdate = useCallback(() => {
    if (!jobApplication || applying) return;
    setApplying(true);

    const formData = new FormData();
    formData.append("authenticity_token", authenticity_token);
    formData.append("job_application[cover_letter]", coverLetter);
    if (resumeFile) {
      formData.append("job_application[resume]", resumeFile);
    }

    sh.patch(`/internal/job_applications/${jobApplication.id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
      .then(() => {
        enqueueSnackbar("Job application updated", { variant: "success" });
        handleClose();
        onApplied?.();
      })
      .catch(() => enqueueSnackbar("Failed to update job application", { variant: "error" }))
      .finally(() => setApplying(false));
  }, [jobApplication, applying, authenticity_token, coverLetter, resumeFile, enqueueSnackbar, onApplied]);

  const handleApply = useCallback(
    (jobOfferIds) => {
      if ((!effectiveCandidateId && !isBulkMode) || applying) return;
      setApplying(true);

      if (isBulkMode) {
        sh.post("/internal/candidates/bulk_apply", {
          authenticity_token,
          candidate_ids: candidateIds,
          job_offer_ids: jobOfferIds,
          cover_letter: coverLetter,
          utm_source: "internal",
        })
          .then((res) => {
            const { message } = res.data;
            enqueueSnackbar(message || "Applications created successfully", { variant: "success" });
            handleClose();
            onApplied?.();
          })
          .catch(() => enqueueSnackbar("Failed to apply", { variant: "error" }))
          .finally(() => setApplying(false));
      } else {
        const formData = new FormData();
        formData.append("authenticity_token", authenticity_token);
        formData.append("cover_letter", coverLetter);
        formData.append("utm_source", "internal");
        jobOfferIds.forEach((id) => formData.append("job_offer_ids[]", id));
        if (resumeFile) {
          formData.append("resume", resumeFile);
        } else if (effectiveResumeUrl) {
          formData.append("resume", effectiveResumeUrl);
        }

        sh.post(`/internal/candidates/${effectiveCandidateId}/job_applications`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
          .then((res) => {
            const { applied, skipped } = res.data;
            if (applied.length > 0) enqueueSnackbar(`Applied to ${applied.length} offer${applied.length > 1 ? "s" : ""}`, { variant: "success" });
            if (skipped.length > 0) enqueueSnackbar(`${skipped.length} already applied — skipped`, { variant: "info" });
            handleClose();
            onApplied?.();
          })
          .catch(() => enqueueSnackbar("Failed to apply", { variant: "error" }))
          .finally(() => setApplying(false));
      }
    },
    [effectiveCandidateId, effectiveResumeUrl, candidateIds, isBulkMode, applying, authenticity_token, coverLetter, resumeFile, enqueueSnackbar, onApplied],
  );

  const handleSpontaneousApply = useCallback(
    (companyIds) => {
      if ((!effectiveCandidateId && !isBulkMode) || applying) return;
      setApplying(true);

      if (isBulkMode) {
        sh.post("/internal/candidates/spontaneous_apply", {
          authenticity_token,
          candidate_ids: candidateIds,
          company_ids: companyIds,
          cover_letter: coverLetter,
          utm_source: "internal",
        })
          .then((res) => {
            const { message } = res.data;
            enqueueSnackbar(message || "Spontaneous applications created", { variant: "success" });
            handleClose();
            onApplied?.();
          })
          .catch(() => enqueueSnackbar("Failed to apply", { variant: "error" }))
          .finally(() => setApplying(false));
      } else {
        const formData = new FormData();
        formData.append("authenticity_token", authenticity_token);
        formData.append("candidate_id", effectiveCandidateId);
        formData.append("cover_letter", coverLetter);
        formData.append("utm_source", "internal");
        companyIds.forEach((id) => formData.append("company_ids[]", id));
        if (resumeFile) {
          formData.append("resume", resumeFile);
        } else if (effectiveResumeUrl) {
          formData.append("resume", effectiveResumeUrl);
        }

        sh.post("/internal/candidates/spontaneous_apply", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
          .then((res) => {
            const { message } = res.data;
            enqueueSnackbar(message || "Spontaneous applications created", { variant: "success" });
            handleClose();
            onApplied?.();
          })
          .catch(() => enqueueSnackbar("Failed to apply", { variant: "error" }))
          .finally(() => setApplying(false));
      }
    },
    [effectiveCandidateId, effectiveResumeUrl, candidateIds, isBulkMode, applying, authenticity_token, coverLetter, resumeFile, enqueueSnackbar, onApplied],
  );

  const isApplyDisabled = (isCreateMode && !selectedCandidate) || applying || (
    tabValue === 0 ? selectedOfferIds.length === 0 : selectedCompanyIds.length === 0
  );

  const applyCount = tabValue === 0 ? selectedOfferIds.length : selectedCompanyIds.length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { borderRadius: "12px", minWidth: 560, maxHeight: "80vh" } }}
    >
      <DialogTitle sx={{ fontSize: 16, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)", pb: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {isEditMode ? "Edit Job Application" : isBulkMode ? `Apply ${candidateIds.length} candidates` : isCreateMode ? "New Job Application" : "Apply to"}
        {!isEditMode && (
          <FormControlLabel
            control={
              <Switch
                checked={tabValue === 1}
                onChange={(e) => setTabValue(e.target.checked ? 1 : 0)}
                size="small"
                sx={{ "& .MuiSwitch-switchBase:hover": { backgroundColor: "transparent" } }}
              />
            }
            label={<Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>{tabValue === 1 ? "Spontaneous" : "Job Offer"}</Typography>}
            labelPlacement="start"
            sx={{ mr: 0, gap: 0.5 }}
          />
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {isEditMode ? (
          <>
            <Box sx={{ mb: 1.5, mt: 1 }}>
              <Typography sx={{ fontSize: 12, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", mb: 0.5 }}>
                CV
              </Typography>
              <input
                ref={cvInputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                style={{ display: "none" }}
                onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
              />
              <Box
                component="button"
                type="button"
                onClick={() => cvInputRef.current?.click()}
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  height: 40,
                  borderRadius: "6px",
                  border: "1px solid hsl(140 8% 88%)",
                  backgroundColor: "hsl(140 10% 95%)",
                  fontSize: 14,
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  color: resumeFile || (fullJobApplication || jobApplication).resume_url ? "hsl(150 30% 12%)" : "hsl(150 10% 46%)",
                  cursor: "pointer",
                  transition: "background-color 0.15s ease",
                  "&:hover": { backgroundColor: "hsla(140, 8%, 90%, 0.3)" },
                }}
              >
                <Upload size={16} style={{ flexShrink: 0 }} />
                {resumeFile
                  ? resumeFile.name
                  : (fullJobApplication || jobApplication).resume_url
                    ? "Current CV attached"
                    : "Upload CV (PDF, DOC)"}
              </Box>
            </Box>

            <Typography sx={{ fontSize: 12, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", mb: 0.5 }}>
              Cover letter
            </Typography>
            <TextField
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              size="small"
              sx={{
                mb: 1.5,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: 13,
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(140 8% 88%)" },
                },
              }}
            />
          </>
        ) : (
          <>
            {isCreateMode && (
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: 12, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", mb: 0.5 }}>
                  Candidate
                </Typography>
                {selectedCandidate ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 1.5,
                      py: 1,
                      borderRadius: "6px",
                      border: "1px solid hsl(140 8% 88%)",
                      backgroundColor: "hsl(140 10% 95%)",
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
                        {selectedCandidate.full_name}
                      </Typography>
                      <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
                        {selectedCandidate.email}
                      </Typography>
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => { setSelectedCandidate(null); setCandidateSearch(""); setCandidateResults([]); }}
                      sx={{ background: "none", border: "none", cursor: "pointer", color: "hsl(150 10% 46%)", fontSize: 18, lineHeight: 1, p: 0 }}
                    >
                      ×
                    </Box>
                  </Box>
                ) : (
                  <>
                    <TextField
                      placeholder="Search by name or email..."
                      value={candidateSearch}
                      onChange={(e) => {
                        setCandidateSearch(e.target.value);
                        const q = e.target.value.trim();
                        if (q.length < 2) { setCandidateResults([]); return; }
                        setSearchingCandidates(true);
                        sh.get("/internal/search/candidates", { params: { q, scope: "all" } })
                          .then((res) => {
                            const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
                            setCandidateResults(data.map((c) => ({
                              ...c,
                              full_name: `${c.first_name || ""} ${c.last_name || ""}`.trim(),
                            })));
                          })
                          .catch(() => setCandidateResults([]))
                          .finally(() => setSearchingCandidates(false));
                      }}
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          fontSize: 14,
                          fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(140 8% 88%)" },
                        },
                      }}
                    />
                    {searchingCandidates && (
                      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                        <CircularProgress size={20} sx={{ color: "hsl(150 38% 17%)" }} />
                      </Box>
                    )}
                    {!searchingCandidates && candidateSearch.trim().length >= 2 && candidateResults.length === 0 && (
                      <Typography sx={{ fontSize: 13, color: "hsl(150 10% 46%)", textAlign: "center", py: 2 }}>No candidates found</Typography>
                    )}
                    <List disablePadding sx={{ mt: 1, maxHeight: 350, overflowY: "auto" }}>
                      {candidateResults.map((c) => (
                        <ListItemButton
                          key={c.id}
                          onClick={() => { setSelectedCandidate(c); setCandidateSearch(""); setCandidateResults([]); }}
                          disableRipple
                          sx={{
                            borderRadius: "8px",
                            mb: 0.5,
                            "&:hover": { backgroundColor: "hsl(140 10% 95%)" },
                          }}
                        >
                          <ListItemText
                            primary={c.full_name}
                            primaryTypographyProps={{ sx: { fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" } }}
                            secondary={c.email}
                            secondaryTypographyProps={{ sx: { fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui" } }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </>
                )}
              </Box>
            )}

            {!isBulkMode && (
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: 12, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", mb: 0.5 }}>
              CV
                </Typography>
                <input
                  ref={cvInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  style={{ display: "none" }}
                  onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
                />
                <Box
                  component="button"
                  type="button"
                  onClick={() => cvInputRef.current?.click()}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    height: 40,
                    borderRadius: "6px",
                    border: "1px solid hsl(140 8% 88%)",
                    backgroundColor: "hsl(140 10% 95%)",
                    fontSize: 14,
                    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                    color: resumeFile || effectiveResumeUrl ? "hsl(150 30% 12%)" : "hsl(150 10% 46%)",
                    cursor: "pointer",
                    transition: "background-color 0.15s ease",
                    "&:hover": { backgroundColor: "hsla(140, 8%, 90%, 0.3)" },
                  }}
                >
                  <Upload size={16} style={{ flexShrink: 0 }} />
                  {resumeFile
                    ? resumeFile.name
                    : effectiveResumeUrl
                      ? `Candidate's CV (${effectiveResumeUrl.split("/").pop().split("?")[0] || "attached"})`
                      : "Upload CV (PDF, DOC)"}
                </Box>
              </Box>
            )}

            {isBulkMode && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5, p: 1, borderRadius: "8px", backgroundColor: "hsl(210 40% 96%)" }}>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: "hsl(210 50% 45%)" }} />
                <Typography sx={{ fontSize: 12, color: "hsl(210 30% 30%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
              Each candidate's existing CV will be attached automatically.
                </Typography>
              </Box>
            )}

            <Typography sx={{ fontSize: 12, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", mb: 0.5 }}>
          Cover letter
            </Typography>
            <TextField
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              size="small"
              sx={{
                mb: 1.5,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: 13,
                  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(140 8% 88%)" },
                },
              }}
            />

            {tabValue === 0 && (
              <>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", mb: 0.5 }}>
              Job offer
                </Typography>
                <TextField
                  placeholder="Search by job title or company..."
                  value={offerSearch}
                  onChange={(e) => {
                    setOfferSearch(e.target.value);
                    const q = e.target.value.trim();
                    if (q.length < 3) { setOfferResults([]); return; }
                    setSearchingOffers(true);
                    sh.get("/internal/search/job_offers", { params: { q } })
                      .then((res) => setOfferResults(res.data || []))
                      .catch(() => setOfferResults([]))
                      .finally(() => setSearchingOffers(false));
                  }}
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      fontSize: 14,
                      fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(140 8% 88%)" },
                    },
                  }}
                />
                {searchingOffers && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={20} sx={{ color: "hsl(150 38% 17%)" }} />
                  </Box>
                )}
                {!searchingOffers && offerSearch.trim().length >= 3 && offerResults.length === 0 && (
                  <Typography sx={{ fontSize: 13, color: "hsl(150 10% 46%)", textAlign: "center", py: 2 }}>No offers found</Typography>
                )}
                <List disablePadding sx={{ mt: 1 }}>
                  {(() => {
                    const resultsFiltered = offerResults.filter((jo) => !appliedOfferIds.includes(jo.id));
                    const pinnedSelected = selectedOffers.filter(
                      (jo) => !appliedOfferIds.includes(jo.id) && !resultsFiltered.some((r) => r.id === jo.id),
                    );
                    return [...pinnedSelected, ...resultsFiltered];
                  })()
                    .map((jo) => {
                      const selected = selectedOfferIds.includes(jo.id);
                      return (
                        <ListItemButton
                          key={jo.id}
                          onClick={() => toggleOfferSelection(jo)}
                          disableRipple
                          sx={{
                            borderRadius: "8px",
                            mb: 0.5,
                            backgroundColor: selected ? "hsla(150, 38%, 17%, 0.08)" : "transparent",
                            "&:hover": { backgroundColor: selected ? "hsla(150, 38%, 17%, 0.12)" : "hsl(140 10% 95%)" },
                          }}
                        >
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: "4px",
                              border: selected ? "none" : "1.5px solid hsl(150 10% 46%)",
                              backgroundColor: selected ? "hsl(150 38% 17%)" : "transparent",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mr: 1,
                              flexShrink: 0,
                            }}
                          >
                            {selected && <CheckIcon size={12} color="#fff" strokeWidth={3} />}
                          </Box>
                          <ListItemText
                            primary={jo.title}
                            primaryTypographyProps={{ sx: { fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" } }}
                            secondary={jo.company}
                            secondaryTypographyProps={{ sx: { fontSize: 12, fontFamily: "Segoe UI Regular, sans-serif, system-ui" } }}
                          />
                        </ListItemButton>
                      );
                    })}
                </List>
              </>
            )}

            {tabValue === 1 && (
              <>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", mb: 0.5 }}>
              Company
                </Typography>
                <TextField
                  placeholder="Search for a company..."
                  value={companySearch}
                  onChange={(e) => {
                    setCompanySearch(e.target.value);
                    const q = e.target.value.trim();
                    if (q.length < 2) { setCompanyResults([]); return; }
                    setSearchingCompanies(true);
                    sh.get("/internal/search/companies", { params: { q } })
                      .then((res) => setCompanyResults(res.data || []))
                      .catch(() => setCompanyResults([]))
                      .finally(() => setSearchingCompanies(false));
                  }}
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      fontSize: 14,
                      fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "hsl(140 8% 88%)" },
                    },
                  }}
                />
                {searchingCompanies && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={20} sx={{ color: "hsl(150 38% 17%)" }} />
                  </Box>
                )}
                {!searchingCompanies && companySearch.trim().length >= 2 && companyResults.length === 0 && (
                  <Typography sx={{ fontSize: 13, color: "hsl(150 10% 46%)", textAlign: "center", py: 2 }}>No companies found</Typography>
                )}
                <List disablePadding sx={{ mt: 1 }}>
                  {(() => {
                    const pinnedSelected = selectedCompanies.filter(
                      (c) => !companyResults.some((r) => r.id === c.id),
                    );
                    return [...pinnedSelected, ...companyResults];
                  })().map((company) => {
                    const selected = selectedCompanyIds.includes(company.id);
                    return (
                      <ListItemButton
                        key={company.id}
                        onClick={() => toggleCompanySelection(company)}
                        disableRipple
                        sx={{
                          borderRadius: "8px",
                          mb: 0.5,
                          backgroundColor: selected ? "hsla(150, 38%, 17%, 0.08)" : "transparent",
                          "&:hover": { backgroundColor: selected ? "hsla(150, 38%, 17%, 0.12)" : "hsl(140 10% 95%)" },
                        }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "4px",
                            border: selected ? "none" : "1.5px solid hsl(150 10% 46%)",
                            backgroundColor: selected ? "hsl(150 38% 17%)" : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 1,
                            flexShrink: 0,
                          }}
                        >
                          {selected && <CheckIcon size={12} color="#fff" strokeWidth={3} />}
                        </Box>
                        <ListItemText
                          primary={company.name}
                          primaryTypographyProps={{ sx: { fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" } }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none", fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
          Cancel
        </Button>
        {isEditMode ? (
          <Button
            disabled={applying}
            onClick={handleUpdate}
            sx={{
              textTransform: "none",
              fontSize: 14,
              fontWeight: 600,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              borderRadius: "0.5rem",
              px: 2,
              backgroundColor: "hsl(150 38% 17%)",
              color: "#fff",
              "&:hover": { backgroundColor: "hsl(150 38% 22%)" },
              "&.Mui-disabled": { backgroundColor: "hsl(140 10% 85%)", color: "hsl(150 10% 60%)" },
            }}
          >
            {applying ? "Saving…" : "Save"}
          </Button>
        ) : (
          <Button
            disabled={isApplyDisabled}
            onClick={() => tabValue === 0 ? handleApply(selectedOfferIds) : handleSpontaneousApply(selectedCompanyIds)}
            sx={{
              textTransform: "none",
              fontSize: 14,
              fontWeight: 600,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              borderRadius: "0.5rem",
              px: 2,
              backgroundColor: "hsl(150 38% 17%)",
              color: "#fff",
              "&:hover": { backgroundColor: "hsl(150 38% 22%)" },
              "&.Mui-disabled": { backgroundColor: "hsl(140 10% 85%)", color: "hsl(150 10% 60%)" },
            }}
          >
            {applying ? "Applying…" : `Apply (${applyCount})`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ApplyToOfferDialog;
